/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { IModelApp, IModelConnection, ScreenViewport} from "@itwin/core-frontend";
import { CreateTextureArgs, TextureImage} from "@itwin/core-frontend";
import { GraphicBuilder, imageElementFromUrl } from "@itwin/core-frontend";
import { ColorDef,RenderMaterial, TextureMapping} from "@itwin/core-common";
import { IndexedPolyface, PolyfaceBuilder, StrokeOptions} from "@itwin/core-geometry";
import { Arc3d, Box, Point3d, Range3d, Sphere, Transform} from "@itwin/core-geometry";

import { cMarker, MarkerData } from "./Marker";
import { cIssueDlg } from "./IssuesDlg";
import { cIssuesApi } from "./IssuesApi";
import { cView } from "./View";
import { cViewClip } from "./ViewClip";
import { cIssuesTable } from "./IssuesTable";
import { cIssuesToolbar } from "./IssuesPopup";
import { cMessage } from "./Message";

export enum IssueMarkerType {
  Icon = 1,
  Graphics = 2,
}

export class IssueData {
  public type: string;
  public id: number;
  public title: string;
  public description: string;
  public status: string;
  public priority: string;
  public createdBy?: string;
  public timeCreated?: string;
  public lastModifiedBy?: string;
  public lastModified?: string;
  public assignedTo: string;
  public location: Point3d;
  public dbKey?: string;
  public eyePoint: Point3d;
  public targetPoint: Point3d;
  public clipRange?: Range3d;

  constructor() {
    this.type = "";
    this.priority = "";
    this.id = 0;
    this.title = "";
    this.description = "";
    this.status = "";
    this.assignedTo = "";
    this.location = Point3d.create(0, 0, 0);
    this.eyePoint = Point3d.create(0, 0, 0);
    this.targetPoint = Point3d.create(0, 0, 0);
    this.clipRange = Range3d.createNull();
  }

  public setLocation = (point: Point3d) => {
    this.location.setFromPoint3d(point);
  }
}

export class cIssue {
  private static iModel: IModelConnection;
  public static sViewPort: ScreenViewport;

  public static _images: Map<string, HTMLImageElement>;
  //public static _imagesP: Map<string, HTMLImageElement>;
  public static _issueId: number;
  public static _issuesList: Map<number, IssueData>;
  private static _colors: Map<string, ColorDef>;
  private static _materials: Map<string, RenderMaterial>;

  private static _iconType: IssueMarkerType;
  private static _showIssueLabel: boolean;

  public static initIssue = async (vp: ScreenViewport, imodel: IModelConnection) => {
    this.iModel = imodel;
    this.sViewPort = vp;

    this._issueId = 1000; //start from 1000
    this._showIssueLabel = false;

    this.initColors();
    await this.initIcons();
    await this.initMaterials();

    cMarker.createMarkerSet("Issues");
    //    cMarker.setDrawFunction("Issues", cIssue.drawCallback);
    cMarker.setClickCallback("Issues", cIssue.onPinSelected);
    cMarker.setToolbar("Issues", new cIssuesToolbar());

    this.setMarkerImageType(IssueMarkerType.Icon);

    await this.getProjectIssues(this.iModel.iTwinId!);
    cMessage.clearStatusBarMessage();
  }

  private static initColors = () => {
    this._colors = new Map();
    this._colors.set("Comment", ColorDef.from(79, 152, 210));
    this._colors.set("Defect", ColorDef.from(138, 83, 161));
    this._colors.set("Follow-up", ColorDef.from(254, 224, 84));
    this._colors.set("Maintenance", ColorDef.from(93, 45, 144));
    this._colors.set("Other", ColorDef.from(114, 193, 102));
  }

  private static initIcons = async () => {
    this._images = new Map();
    this._images.set("Comment",await imageElementFromUrl("./image/comment.svg"));
    this._images.set("Defect", await imageElementFromUrl("./image/defect.svg"));
    this._images.set("Follow-up", await imageElementFromUrl("./image/follow-up.svg"));
    this._images.set("Maintenance", await imageElementFromUrl("./image/maintenance.svg"));
    this._images.set("Other", await imageElementFromUrl("./image/other.svg"));
    // priority
    this._images.set("Comment+P", await imageElementFromUrl("./image/comment-p.svg"));
    this._images.set("Defect+P", await imageElementFromUrl("./image/defect-p.svg"));
    this._images.set("Follow-up+P", await imageElementFromUrl("./image/follow-up-p.svg"));
    this._images.set("Maintenance+P", await imageElementFromUrl("./image/maintenance-p.svg"));
    this._images.set("Other+P", await imageElementFromUrl("./image/other-p.svg"));
  }

  private static initMaterials = async () => {
    this._materials = new Map();
    this._materials.set("Comment", await this.createMaterial("Comment", "./image/comment-T.svg"));
    this._materials.set("Defect", await this.createMaterial("Defect", "./image/defect-T.svg"));
    this._materials.set("Follow-up", await this.createMaterial("Follow-up", "./image/follow-up-T.svg"));
    this._materials.set("Maintenance", await this.createMaterial("Maintenance", "./image/maintenance-T.svg"));
    this._materials.set("Other", await this.createMaterial("Other", "./image/other-T.svg"));
    // priority
    this._materials.set("Comment+P", await this.createMaterial("Comment+P", "./image/comment-TP.svg"));
    this._materials.set("Defect+P", await this.createMaterial("Defect+P", "./image/defect-TP.svg"));
    this._materials.set("Follow-up+P", await this.createMaterial("Follow-up+P", "./image/follow-up-TP.svg"));
    this._materials.set("Maintenance+P", await this.createMaterial("Maintenance+P", "./image/maintenance-TP.svg"));
    this._materials.set("Other+P", await this.createMaterial("Other+P", "./image/other-TP.svg"));
  }

  private static createMaterial = async (name: string, imageURL: string): Promise<RenderMaterial> => {
    let renderMaterial: RenderMaterial;

    const image = await imageElementFromUrl(imageURL);
    if (image) {
      const textureImage: TextureImage = { source: image };
      const textureArgs: CreateTextureArgs = { image: textureImage };

      const texture = IModelApp.renderSystem.createTexture(textureArgs);
      if (texture) {
        const textureMapping = new TextureMapping(texture, new TextureMapping.Params({}));
        const materialParams = new RenderMaterial.Params();
        materialParams.textureMapping = textureMapping;
        materialParams.key = name;
        renderMaterial = IModelApp.renderSystem.createMaterial(
          materialParams,
          IModelApp.viewManager.selectedView!.iModel
        )!;
      }
    }

    return renderMaterial!;
  }

  public static getIssuesList = (): Map<number, IssueData> => {
    return this._issuesList;
  }

  public static nextIssueId = async (update: boolean): Promise<number> => {
    // const nextId = this._issueId + 1;
    // if (update) this._issueId = nextId;
    var nextId = await cIssuesApi.getIssueCount();
    if (nextId > 0) {
      nextId++;
      if (update) {
        if (!(await cIssuesApi.setIssueCount(nextId))) {
          return 0;
        }
      }
    }

    return nextId;
  }

  public static getProjectIssues = async (projectId: string) => {
    cMessage.showStatusBarMessage("Retrieving events...", "icon-cloud-download");
    this._issuesList = new Map();
    await cIssuesApi.getProjectIssues(projectId, this._issuesList);
  }

  public static addNewIssue = async (issue: IssueData) => {
    issue.id = await this.nextIssueId(true);
    cMessage.showStatusBarMessage(`Storing event ${issue.id}...`, "icon-cloud-upload");
    cView.getCameraProps(issue.eyePoint, issue.targetPoint);
    issue.clipRange = cViewClip.getClipRange();

    //    issue.createdBy = userManager.user.email;       //!!OA userManager not available
    //    issue.lastModifiedBy = userManager.user.email;  //!!OA userManager not available

    const d = new Date();
    issue.timeCreated = d.toJSON();
    issue.lastModified = d.toJSON();

    this._issuesList.set(issue.id, issue);
    //    this.addMarker(issue);

    const success = await cIssuesApi.addIssue(issue);
    if (success) {
      this._issuesList.set(issue.id, issue);
      this.addMarker(issue);
    } else {
      // notify: issue could not be saved
      cMessage.showStatusBarError("Access Error: Unable to store event...");
    }

    cMessage.clearStatusBarMessage();
  }

  public static updateIssue = async (issue: IssueData) => {
    cMessage.showStatusBarMessage("Updating event...", "icon-cloud-upload");
    const success = await cIssuesApi.updateIssue(issue);
    if (success) {
      this._issuesList.set(issue.id, issue);
      cMarker.removeMarker("Issues", issue.id.toString());
      this.addMarker(issue);
      cIssuesTable.updateTable();
    }

    cMessage.clearStatusBarMessage();
  }

  public static deleteIssue = async (issueId: number) => {
    cMessage.showStatusBarMessage("Removing event...", "icon-cloud");
    const issue = this.getIssueById(issueId);
    const success = await cIssuesApi.deleteIssue(issue);
    if (success) {
      this._issuesList.delete(issueId);
      cMarker.removeMarker("Issues", issueId.toString());
      cIssuesTable.updateTable();
    }

    cMessage.clearStatusBarMessage();
  }

  public static showFilter = (criteria: IssueData) => {
    let found = 0;
    this.removeAllMarkers();
    for (const [id, issue] of this._issuesList) {
      if (this.applyFilter(issue, criteria)) {
        this.addMarker(issue);
        found += 1;
      }
    }

    if (found === 0) {
      cMessage.showStatusBarError("No Event found...");
    } else {
      cMessage.showStatusBarMessage(`${found} Event(s) found`, "icon icon-issue-resolution");
    }
  }

  public static applyFilter = (issue: IssueData, criteria: IssueData): boolean => {
    if (criteria.type !== "all" && criteria.type !== issue.type.toLowerCase()) {
      return false;
    }

    if (criteria.priority !== "all" && criteria.priority !== issue.priority.toLowerCase()) {
      return false;
    }

    if (criteria.status !== "all" && criteria.status !== issue.status.toLowerCase()) {
      return false;
    }

    if (criteria.timeCreated !== "") {
      //!!OA both dates are strings - extract dates and compare for greater than
      // if (issue.timeCreated! < criteria.timeCreated!) {
      //         return(false);
      // }
    }

    if (criteria.assignedTo !== "all" && criteria.assignedTo !== "") {
      const assigned = criteria.assignedTo.toLowerCase();
      if (!issue.assignedTo.toLowerCase().includes(assigned)) {
        return false;
      }
    }

    return true;
  }

  public static searchText = (keyword: string) => {
    let found = 0;
    this.removeAllMarkers();
    for (const [id, issue] of this._issuesList) {
      if (this.findText(issue, keyword)) {
        this.addMarker(issue);
        found += 1;
      }
    }

    if (found === 0) {
      cMessage.showStatusBarError("No Event found...");
    } else {
      cMessage.showStatusBarMessage(`${found} Event(s) found`,"icon icon-issue-resolution" );
    }
  }

  private static findText = (issue: IssueData, searchString: string): boolean => {
    // extract individual keywords in search string
    const searchKeys = searchString.toLowerCase().split(" ");

    // loop through all properties of IssueData object
    const props = Object.entries(issue);
    for (const [prop, value] of props) {
      if (value === undefined) continue;

      var val;
      switch (prop) {
        case "id":
          val = value.toString();
          break;
        case "location":
        case "eyePoint":
        case "targetPoint":
          continue; // skip - Point3d
        case "clipRange":
          continue; // skip - Range3d
        default:
          val = value.toLowerCase();
      }

      // look for keywords in current property
      for (const keyword of searchKeys) {
        keyword.trim();
        if (keyword !== "" && val.includes(keyword)) {
          return true;
        }
      }
    }

    return false;
  }

  public static showAllMarkers = () => {
    this.removeAllMarkers();
    this._issuesList.forEach((issue: IssueData) => {
      this.addMarker(issue);
    });
  }

  public static removeAllMarkers = () => {
    cMarker.removeAllMarkers("Issues");
  }

  public static getIssueById = (issueId: number): IssueData => {
    const issue = this._issuesList.get(issueId)!;

    return issue;
  }

  private static addMarker = (issue: IssueData) => {
    const markerData = this.getMarkerData(issue);
    if (this._iconType === IssueMarkerType.Icon) {
      cMarker.addMarkerPoint("Issues", markerData, 0);
    } else {
      cMarker.addMarkerPoint("Issues", markerData);
    }
  }

  private static getIcon = (name: string): HTMLImageElement => {
    let image: HTMLImageElement;
    image = this._images.get(name)!;
    if (!image) image = this._images.get("Other")!;
    return image;
  }

  private static getColor = (name: string): ColorDef => {
    let color: ColorDef;
    color = cIssue._colors.get(name)!;
    if (!color) color = cIssue._colors.get("Other")!;
    return color;
  }

  private static getMaterial = (name: string): RenderMaterial => {
    let material: RenderMaterial;
    material = cIssue._materials.get(name)!;
    if (!material) material = cIssue._materials.get("Other")!;
    return material;
  }

  public static showLabel = (showHide: boolean) => {
    this._showIssueLabel = showHide;
    this.showAllMarkers();
  }

  public static drawCallback = (builder: GraphicBuilder, issueId: number): IndexedPolyface => {
    const issue = cIssue.getIssueById(Number(issueId)!);

    const options = StrokeOptions.createForCurves();
    options.needParams = false;
    options.needNormals = true;
    const pFaceBuilder = PolyfaceBuilder.create(options);

    // draw sphere
    const shape = Sphere.createCenterRadius(new Point3d(0, 0, 0), 0.2);
    pFaceBuilder.addSphere(shape);

    // draw a cube
    // const range = Range3d.createXYZXYZ(-0.2, -0.2, -0.2, 0.2, 0.2, 0.2);
    // const shape = Box.createRange(range, true);
    // pFaceBuilder.addBox(shape!);

    let color: ColorDef;
    if (issue.priority === "High") {
      color = ColorDef.red;
    } else {
      color = cIssue.getColor(issue.type);
    }
    builder.setSymbology(ColorDef.white, color, 2);

    return pFaceBuilder.claimPolyface(false);
  };

  public static onPinSelected = (issueId: number) => {
    const issue = cIssue.getIssueById(Number(issueId)!);
    if (issue) {
      cIssueDlg.updateIssue(issue);
    }
  };

  public static getMarkerData = (issue: IssueData): MarkerData => {
    const markerData = new MarkerData();

    let type = issue.type;
    if (issue.priority === "High") type += "+P";

    markerData.id = issue.id.toString();
    markerData.type = type;
    markerData.point = issue.location!;
    markerData.title = issue.id.toString() + ": " + issue.title;
    markerData.description = issue.description;

    if (this._showIssueLabel) markerData.label = issue.id.toString();
    if (this._iconType === IssueMarkerType.Icon) {
      markerData.image = this.getIcon(type);
    } else {
      markerData.material = this.getMaterial(type);
    }

    markerData.data = issue;

    return markerData;
  }

  public static setMarkerImageType = (type: IssueMarkerType) => {
    this._iconType = type;
    if (type === IssueMarkerType.Graphics) {
      cMarker.setDrawFunction("Issues", cIssue.drawCallback);
    } else {
      cMarker.setDrawFunction("Issues", 0);
    }
  }
}
